import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["card", "preview", "previewName", "avatar", "bgWrapper", "bgUploadLabel", "bgRemoveBtn"]

  // --- Settings page: card selection ---

  select({ params: { slug } }) {
    this.cardTargets.forEach(card => {
      const isSelected = card.dataset.slug === slug
      card.classList.toggle("border-accent/40", isSelected)
      card.classList.toggle("bg-accent/3", isSelected)
      card.classList.toggle("border-border-lt", !isSelected)
    })

    const previewEl = this.previewTarget
    if (previewEl.dataset.avatarPersonaValue !== slug) {
      previewEl.dataset.avatarPersonaValue = slug
    }

    const selectedCard = this.cardTargets.find(c => c.dataset.slug === slug)
    if (selectedCard && this.hasPreviewNameTarget) {
      this.previewNameTarget.textContent = selectedCard.querySelector("h4")?.textContent || slug
    }
  }

  // --- Load a Model3d by swapping the avatar's assetsUrl ---

  loadModel3d(event) {
    const url = event.currentTarget.dataset.assetsUrl
    const name = event.currentTarget.dataset.modelName
    if (!url) return

    const avatarEl = this._avatarElement()
    if (!avatarEl) return

    if (this.hasPreviewNameTarget) {
      this.previewNameTarget.textContent = name || ""
    }

    // Update assetsUrl which triggers the avatar controller to reload
    avatarEl.dataset.avatarAssetsUrlValue = url
  }

  // --- Preview uploaded file locally (before save) ---

  previewUpload(event) {
    const file = event.currentTarget.files?.[0]
    if (!file) return

    const blobUrl = URL.createObjectURL(file)

    // Hide placeholder
    const placeholder = document.getElementById("model-placeholder")
    if (placeholder) placeholder.classList.add("hidden")

    // Deselect any library model card
    this.element.querySelectorAll("[data-model-card]").forEach(c => {
      c.classList.remove("border-accent", "shadow-sm")
    })

    // Clear the model3d_id hidden field since this is a new upload
    const hiddenField = document.getElementById("selected-model3d-id")
    if (hiddenField) hiddenField.value = ""

    this._updateModelLabels(file.name.replace(/\.(glb|vrm)$/i, ""))

    // Inject a temporary blob: assets endpoint and trigger avatar load
    const avatarEl = this._avatarElement()
    if (!avatarEl) return

    // Store blob URL so the avatar controller can use it
    avatarEl._localBlobUrl = blobUrl
    avatarEl.dataset.avatarAssetsUrlValue = "blob:" + Date.now()
  }

  // --- Select a Model3d from a card click ---

  selectModel3d(event) {
    event.preventDefault()
    const card = event.currentTarget
    const id = card.dataset.model3dId
    const url = card.dataset.assetsUrl
    const name = card.dataset.modelName
    if (!url || !id) return

    // Update hidden field
    const hiddenField = document.getElementById("selected-model3d-id")
    if (hiddenField) hiddenField.value = id

    // Highlight selected card
    this.element.querySelectorAll("[data-model-card]").forEach(c => {
      c.classList.toggle("border-accent", c.dataset.model3dId === id)
      c.classList.toggle("shadow-sm", c.dataset.model3dId === id)
    })

    this._updateModelLabels(name)

    // Hide placeholder
    const placeholder = document.getElementById("model-placeholder")
    if (placeholder) placeholder.classList.add("hidden")

    // Load model in preview
    const avatarEl = this._avatarElement()
    if (avatarEl) avatarEl.dataset.avatarAssetsUrlValue = url
  }

  // --- Switch model from a select dropdown ---

  switchModel(event) {
    const selected = event.currentTarget.selectedOptions?.[0]
    if (!selected) return

    const url = selected.dataset.assetsUrl
    const name = selected.dataset.modelName
    if (!url) return

    const avatarEl = this._avatarElement()
    if (!avatarEl) return

    if (this.hasPreviewNameTarget) {
      this.previewNameTarget.textContent = name || ""
    }

    const placeholder = document.getElementById("model-placeholder")
    if (placeholder) placeholder.classList.add("hidden")

    avatarEl.dataset.avatarAssetsUrlValue = url
  }

  // --- Play animation by name, with optional URL fallback for on-demand loading ---

  playAnimation(event) {
    const el = event.currentTarget
    // Support both buttons (data attributes) and selects (selectedOptions)
    const name = el.dataset.animation || el.dataset.animationName || el.value
    const url = el.dataset.animationUrl || el.selectedOptions?.[0]?.dataset?.animationUrl
    if (!name) return console.warn("[persona-preview] playAnimation: no name", el)
    if (!url) console.warn("[persona-preview] playAnimation: no url for", name)
    this._playByName(name, url)
  }

  playAnimationFromSelect(event) {
    const btn = event.currentTarget
    const row = btn.parentElement
    const select = row?.querySelector('select')
    if (!select) return
    const selected = select.selectedOptions?.[0]
    const name = select.value || selected?.text?.match(/\((.+)\)/)?.[1]?.replace(/ /g, '-')
    const url = selected?.dataset?.animationUrl
    if (!name) return
    this._playByName(name, url)
  }

  // --- Private ---

  _playByName(name, url) {
    const avatarEl = this._avatarElement()
    const ctrl = avatarEl?.avatarController
    if (!ctrl) return

    if (ctrl._avatar?.anims?.actions[name]) {
      ctrl._avatar.anims.currentAnimName = ''
      ctrl.playAnimation(name)
    } else if (url) {
      // Load on-demand via the Stimulus avatar controller
      const stimulusCtrl = this.application.getControllerForElementAndIdentifier(avatarEl, 'avatar')
      stimulusCtrl?.loadAndPlayFBX(url, name)
    }
  }

  // --- Facial expression preview ---

  previewFacialExpression(event) {
    const expression = event.currentTarget.dataset.expression
    if (!expression) return

    const ctrl = this._avatarController()
    if (!ctrl) return

    ctrl.setFacialEmotion(expression, 0.9)
  }

  captureThumbnail(event) {
    const ctrl = this._avatarController()
    if (!ctrl) return
    ctrl.captureThumbnail()
    const btn = event?.currentTarget
    if (btn) {
      const original = btn.innerHTML
      btn.innerHTML = "Saved ✓"
      btn.disabled = true
      setTimeout(() => { btn.innerHTML = original; btn.disabled = false }, 1500)
    }
  }

  resetFacialExpression() {
    const ctrl = this._avatarController()
    if (!ctrl) return

    ctrl.setFacialEmotion('neutral', 0)
  }

  // --- Background upload preview ---

  previewBackgroundUpload(event) {
    const file = event.currentTarget.files?.[0]
    if (!file) return
    const blobUrl = URL.createObjectURL(file)
    const wrapper = this._bgWrapper()
    if (!wrapper) return
    wrapper.style.backgroundImage = `url(${blobUrl})`
    wrapper.style.backgroundSize = "cover"
    wrapper.style.backgroundPosition = "center"
  }

  // --- Background upload (live, no reload) ---
  // Show the local blob immediately, POST the file to /personas/:id/background
  // in the background, then swap to the persistent URL on success. Server
  // failures roll back to the prior background so the UI doesn't lie.

  async uploadBackground(event) {
    const input = event.currentTarget
    const file = input.files?.[0]
    if (!file) return

    const wrapper = this._bgWrapper()
    const previousStyle = wrapper?.style.cssText || ""
    const blobUrl = URL.createObjectURL(file)
    this._applyBg(wrapper, blobUrl)

    const fd = new FormData()
    fd.append("background_image", file)

    try {
      const data = await this._postBg(input.dataset.bgEndpoint, fd)
      if (data?.url) {
        this._applyBg(wrapper, data.url)
        if (this.hasBgUploadLabelTarget) this.bgUploadLabelTarget.textContent = "Replace Background"
        if (this.hasBgRemoveBtnTarget) this.bgRemoveBtnTarget.classList.remove("hidden")
      }
    } catch (err) {
      console.error("[persona-preview] background upload failed", err)
      if (wrapper) wrapper.style.cssText = previousStyle
    } finally {
      URL.revokeObjectURL(blobUrl)
      input.value = ""
    }
  }

  async removeBackground(event) {
    const btn = event.currentTarget
    const confirmMsg = btn.dataset.confirm
    if (confirmMsg && !confirm(confirmMsg)) return

    const fd = new FormData()
    fd.append("remove", "1")

    try {
      await this._postBg(btn.dataset.bgEndpoint, fd)
      const wrapper = this._bgWrapper()
      if (wrapper) {
        wrapper.style.backgroundImage = ""
        wrapper.style.backgroundSize = ""
        wrapper.style.backgroundPosition = ""
      }
      if (this.hasBgUploadLabelTarget) this.bgUploadLabelTarget.textContent = "Upload Background"
      btn.classList.add("hidden")
    } catch (err) {
      console.error("[persona-preview] background remove failed", err)
    }
  }

  _bgWrapper() {
    if (this.hasBgWrapperTarget) return this.bgWrapperTarget
    return this._avatarElement()?.parentElement
  }

  _applyBg(wrapper, url) {
    if (!wrapper) return
    wrapper.style.backgroundImage = `url(${url})`
    wrapper.style.backgroundSize = "cover"
    wrapper.style.backgroundPosition = "center"
  }

  async _postBg(endpoint, formData) {
    if (!endpoint) throw new Error("missing endpoint")
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Accept": "application/json", "X-CSRF-Token": csrf || "" },
      body: formData,
      credentials: "same-origin"
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json()
  }

  // --- Background toggle ---

  toggleBackground(event) {
    const url = event.currentTarget.dataset.bgUrl
    const avatarEl = this._avatarElement()
    if (!avatarEl || !url) return

    const hasBg = avatarEl.style.backgroundImage.includes('url(')
    if (hasBg) {
      avatarEl.style.backgroundImage = ''
      avatarEl.style.backgroundSize = ''
      avatarEl.style.backgroundPosition = ''
    } else {
      avatarEl.style.backgroundImage = `url(${url})`
      avatarEl.style.backgroundSize = 'cover'
      avatarEl.style.backgroundPosition = 'center'
    }
  }

  // --- Private ---

  _updateModelLabels(name) {
    // Desktop
    const infoEl = document.getElementById("selected-model-info")
    const nameEl = document.getElementById("selected-model-name")
    if (infoEl) infoEl.classList.remove("hidden")
    if (nameEl) nameEl.textContent = name

    // Mobile bar
    const mobileLabel = document.getElementById("mobile-model-label")
    const mobileDot = document.getElementById("mobile-model-dot")
    if (mobileLabel) mobileLabel.textContent = name
    if (mobileDot) mobileDot.style.display = "block"
  }

  _avatarElement() {
    if (this.hasAvatarTarget) return this.avatarTarget
    return this.element.querySelector("[data-controller='avatar']")
  }

  _avatarController() {
    return this._avatarElement()?.avatarController
  }
}
